/**
 * `<CandidatePageSidebar>` — 280px left-rail meta panel for desktop (`≥ lg`).
 *
 * Batch 4 — prototype drift remediation (Cluster B):
 *   - Key Skills cap: 3 tags max (Q5 — hard cap, was 8).
 *   - Download Resume + LinkedIn as brand-gradient pill buttons with SVG
 *     icons (Q3 — `bg-brand-grad` / `bg-[#0A66C2]`).
 *   - Quick-add textarea + "Add Note" button at TOP of Notes subsection
 *     (Q4 — per §8.2 item 6 ordering: textarea → button → label → notes →
 *     "+ N more →" link).
 *
 * Phase 2.5 preserved behaviors:
 *   - Avatar block: `<CandidatePhoto>` primitive (P2-2.5-sidebar-avatar-primitive).
 *   - Notes: `useNotes` cache shared with Notes tab (P2-2.5-sidebar-notes-summary).
 *   - Score ring + rank block, fit chips, AI ranking summary, H13 copy bans.
 *
 * §8.0 shell-fidelity fix (S-1/S-2/S-3/S-5): at `lg+` the sidebar is one of
 * the §8.0 shell's two scroll regions. Its bounded height comes from
 * `.cp-body`'s `min-h-0 + overflow-hidden` — there is NO sticky/`calc()`
 * positioning. Background is `surface-elevated` (= `var(--surf)`), sub-
 * sections separate by the 14px flex gap alone (no hairline dividers).
 *
 * Spec: UI_RECREATION_PROMPT.md §8.0 + §8.2.
 */
import { useState, type JSX } from "react";
import { LINKEDIN_BLUE } from "@/lib/tokens";
import { ScoreRing } from "@/components/common/ScoreRing";
import { Chip } from "@/components/common/Chip";
import { StatusPill } from "@/components/candidates/StatusPill";
import { CandidatePhoto } from "@/components/candidates/CandidatePhoto";
import { relativeTime } from "@/lib/relativeTime";
import { useNotes, type NoteOut } from "@/hooks/queries/notes/useNotes";
import { useCreateNote } from "@/hooks/queries/notes/useCreateNote";
import { useToastStore } from "@/stores/useToastStore";
import type { FitOverall } from "@/lib/fitScore";
import type { TenureSignal } from "@/hooks/queries/candidates/useRanking";
import { useParams } from "react-router-dom";
import { AiRatingWidget } from "@/components/ai/AiRatingWidget";
import { CandidateIdentityEditor } from "@/components/candidate-page/CandidateIdentityEditor";
import { CopyButton } from "@/components/common/CopyButton";

interface SidebarCandidate {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  status: string;
  filename: string | null;
  linkedin_url: string | null;
  storage_key_present: boolean;
  /** Phase 2.5 — current pipeline stage slug for this JD context. */
  pipelineStage?: string | null;
  /**
   * Owning recruiter's user id (candidate `created_by`). Kept on the sidebar
   * envelope because callers already thread it and the owner-or-admin edit gate
   * (`CandidateIdentityEditor`) reads the same value.
   */
  created_by?: string | null;
}

interface SidebarRanking {
  rank_position: number;
  fit_overall: FitOverall;
  fit_score: number | null;
  tenure_signal: TenureSignal;
  summary: string;
  skills_tags: string[];
}

interface CandidatePageSidebarProps {
  candidate: SidebarCandidate;
  ranking: SidebarRanking | null;
  /**
   * D7 fix: total number of ranked candidates in this JD's ranking cohort
   * (the set `ranking.rank_position` is ordered within — per-JD, latest
   * complete run, RLS-tenant-scoped). Renders as the rank denominator
   * ("#1 of {rankTotal}"), replacing the old hardcoded "of 100" placeholder.
   * Null when no ranking cohort exists.
   */
  rankTotal: number | null;
  /** True when a JD context exists (drives ranking block display). */
  jdSelected: boolean;
  /**
   * P2-2.5-sidebar-notes-summary: parent-owned tab-switch handler for the
   * "All notes →" link inside the Notes summary block. When omitted, the
   * link is not rendered (defensive — never strand the user on a dead
   * link). The Candidate page passes `() => onTabChange("notes")`.
   */
  onAllNotesClick?: () => void;
  /** Optional JD context ID for per-(candidate × JD) note scoping. */
  jdId?: string;
  /**
   * B-12: ranking run ID for the rank_one AI rating widget. Parent mounts
   * `useRankingRunByResource` and passes the run_id + model_used down.
   * Undefined when no run exists for this JD context.
   */
  rankOneRunId?: string | null;
  /** B-12: model_used from the ranking run — null means no output to rate. */
  rankOneModelUsed?: string | null;
}

function fitLabel(fit: FitOverall): string {
  return `${fit.charAt(0).toUpperCase() + fit.slice(1)} Fit`;
}

function tenureLabel(t: TenureSignal): string {
  const map: Record<TenureSignal, string> = {
    strong: "Strong Tenure",
    moderate: "Moderate Tenure",
    concern: "Tenure Concern",
  };
  return map[t];
}

function tenureTone(t: TenureSignal): "strong" | "moderate" | "weak" {
  return t === "concern" ? "weak" : t;
}

const SECTION_LABEL =
  "text-2xs font-bold uppercase tracking-[0.14em] text-text-tertiary";
// §8.0 S-3: sidebar sub-sections separate by the `.cp-sidebar` 14px flex
// gap (`gap-3.5` on the `<aside>`) alone — no hairline dividers, no
// section margins/padding (not in canon).

const FIT_TO_SCORE: Record<FitOverall, number> = {
  strong: 78,
  moderate: 62,
  weak: 45,
};

/** Download icon SVG (§8.2 item 5) */
function DownloadIcon(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/** Mail icon SVG (Contact & links — email row, 12px) */
function MailIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  );
}

/** Phone icon SVG (Contact & links — phone row, 12px) */
function PhoneIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/** LinkedIn icon SVG (§8.2 item 5) */
function LinkedInIcon(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

export function CandidatePageSidebar({
  candidate,
  ranking,
  rankTotal,
  jdSelected,
  onAllNotesClick,
  jdId,
  rankOneRunId,
  rankOneModelUsed,
}: CandidatePageSidebarProps): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const addToast = useToastStore((s) => s.addToast);

  // P2-2.5-sidebar-notes-summary: surface last 3 notes for this candidate.
  const notesQ = useNotes({
    candidateId: candidate.id,
    enabled: !!candidate.id,
  });
  // Show last 3 notes (§8.2 item 6: "last 3 notes").
  const recentNotes: NoteOut[] = (notesQ.data?.pages?.[0]?.items ?? []).slice(0, 3);
  // Sum across ALL loaded pages of the infinite notes query — page 0 alone
  // caps at PAGE_SIZE (25), so a candidate with more notes (or additional
  // pages fetched by the Notes tab, which shares this query key) would show a
  // "+N more" count that undercounts everything past the first page.
  const totalNotes = (notesQ.data?.pages ?? []).reduce(
    (sum, page) => sum + page.items.length,
    0,
  );
  const notesLoaded = notesQ.isSuccess;
  const hasNotes = recentNotes.length > 0;

  // Inline note-add state (Q4 — at TOP of Notes subsection).
  const [noteText, setNoteText] = useState("");
  const createNote = useCreateNote();

  const handleAddNote = (): void => {
    const content = noteText.trim();
    if (!content) return;
    createNote.mutate(
      { candidate_id: candidate.id, content, jd_id: jdId },
      {
        onSuccess: () => {
          setNoteText("");
          addToast("Note added.", "success");
        },
        onError: () => {
          // useCreateNote shows its own toast on error.
        },
      },
    );
  };

  const score = ranking
    ? (ranking.fit_score ?? FIT_TO_SCORE[ranking.fit_overall])
    : null;

  // Q5: Key Skills cap at 3 (hard max — no extending to 5 or 8).
  const showSkills =
    ranking != null && ranking.skills_tags && ranking.skills_tags.length > 0;
  const showSummary =
    ranking != null && ranking.summary && ranking.summary.trim() !== "";
  const showLinkedIn = !!candidate.linkedin_url;

  const resumeFilename = candidate.filename ?? "resume.pdf";

  // First name for CopyButton toast/aria labels (matches header derivation).
  const firstName = candidate.name.split(" ")[0] ?? candidate.name;

  return (
    <aside
      data-testid="candidate-page-sidebar"
      // §8.0 `.cp-sidebar`: 280px fixed-width rail, `surface-elevated`
      // background (= `var(--surf)`, matches the header strip — S-2),
      // sub-sections stacked with a 14px flex gap (S-3). At `lg+` it is one
      // of only two scroll regions: bounded height comes naturally from
      // `.cp-body`'s `min-h-0 + overflow-hidden` — no sticky/`calc()` hack
      // (S-1). 4px `scroll-secondary` thumb per §2.5.
      className={[
        "flex w-full flex-col gap-3.5 border-b border-border-default",
        "bg-surface-elevated p-[18px]",
        "lg:w-[280px] lg:flex-shrink-0 lg:border-b-0 lg:border-r",
        "scroll-secondary overflow-x-hidden",
        "lg:overflow-y-auto",
      ].join(" ")}
    >
      {/* Avatar block: photo or initials + name + role/company + pipeline stage.
          P2-2.5-sidebar-avatar-primitive: switched from hand-rolled gradient
          circle to the `<CandidatePhoto>` primitive so a real photo renders
          when one exists; falls back to Avatar-style initials otherwise. */}
      <div
        className="flex flex-col items-center gap-2 text-center"
        data-testid="candidate-sidebar-avatar"
      >
        <CandidatePhoto
          candidateId={candidate.id}
          candidateName={candidate.name}
          size="xl"
        />
        <div>
          <p className="text-base font-semibold text-text-primary">
            {candidate.name}
          </p>
          {/* Pipeline stage pill — only when JD context exists */}
          {jdSelected && candidate.pipelineStage ? (
            <div className="mt-1.5 flex justify-center">
              <StatusPill
                variant="pipeline"
                value={candidate.pipelineStage}
                size="sm"
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Inline identity/contact editor — fixes the "Untitled candidate" trap
          and misparsed contact info. Renders the "Edit details" affordance
          only for the owning recruiter or a tenant admin (null otherwise), so
          the read-only sidebar is unchanged for other viewers. */}
      <CandidateIdentityEditor
        candidateId={candidate.id}
        name={candidate.name}
        linkedinUrl={candidate.linkedin_url}
        email={candidate.email}
        phone={candidate.phone}
        ownerUserId={candidate.created_by}
      />

      {/* RANK block */}
      <div>
        <span className={SECTION_LABEL}>Rank</span>
        {!jdSelected ? (
          /* No JD context — show em-dash, no narration (H13). */
          <div
            className="mt-1 flex items-center gap-3"
            data-testid="candidate-sidebar-no-jd"
          >
            <ScoreRing score={null} size={70} />
            <span
              className="text-2xl text-text-tertiary tabular-nums"
              aria-label="No ranking — select a job context"
            >
              —
            </span>
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-3" data-testid="candidate-sidebar-rank-block">
            <ScoreRing score={score} size={70} />
            <div className="flex flex-col">
              <span
                className="text-3xl font-bold text-accent-secondary tabular-nums"
                data-testid="candidate-sidebar-rank"
              >
                {ranking ? `#${ranking.rank_position}` : "—"}
              </span>
              {ranking && rankTotal != null ? (
              <span
                className="text-xs text-text-tertiary"
                data-testid="candidate-sidebar-rank-total"
              >
                of {rankTotal}
              </span>
            ) : null}
            </div>
          </div>
        )}
      </div>

      {/* Fit chips — only when ranked */}
      {jdSelected && ranking ? (
        <div
          className="flex flex-wrap gap-1.5"
          data-testid="candidate-sidebar-chips"
        >
          <Chip tone={ranking.fit_overall} label={fitLabel(ranking.fit_overall)} />
          <Chip
            tone={tenureTone(ranking.tenure_signal)}
            label={tenureLabel(ranking.tenure_signal)}
          />
        </div>
      ) : null}

      {/* Key Skills (Q5 — CANON 3-tag MAX) — only when ranking exists */}
      {showSkills ? (
        <div
          className="flex flex-col gap-1.5"
          data-testid="candidate-sidebar-skills"
        >
          <span className={SECTION_LABEL}>Key Skills</span>
          <div className="flex flex-wrap gap-1.5">
            {/* Hard cap at 3 — per Q5 and §8.2 item 3 */}
            {ranking!.skills_tags.slice(0, 3).map((s) => (
              <Chip key={s} tone="neutral" label={s} />
            ))}
          </div>
        </div>
      ) : null}

      {/* Fit summary */}
      {showSummary ? (
        <div
          className="flex flex-col gap-1.5"
          data-testid="candidate-sidebar-summary"
        >
          <span className={SECTION_LABEL}>AI Ranking Summary</span>
          {/* §8.0 S-5: `.cp-summary-box` padding is `11px 13px`
              (vertical/horizontal). `bg-surface-card` is correct — a nested
              card inside the `surface-elevated` sidebar. */}
          <p className="cp-summary-box scroll-tight max-h-[110px] overflow-y-auto rounded-lg border border-border-default bg-surface-card px-[13px] py-[11px] text-[11px] leading-[1.6] text-text-secondary">
            {ranking!.summary}
          </p>
          {/* B-12: rank_one rating widget — after summary, gated on JD + run + model */}
          {jdSelected && rankOneRunId && rankOneModelUsed ? (
            <div className="flex justify-end">
              <AiRatingWidget
                actionType="rank_one"
                resourceKey={`rank_one:${rankOneRunId}:${candidate.id}`}
                modelUsed={rankOneModelUsed}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Contact & links section — email/phone text rows precede the action
          pills (HoD spec). Renders when any of email/phone/resume/LinkedIn
          exist; each row is omitted entirely when its value is null. */}
      {candidate.email ||
      candidate.phone ||
      candidate.storage_key_present ||
      showLinkedIn ? (
        <div
          className="flex flex-col gap-[7px]"
          data-testid="candidate-sidebar-contact"
        >
          <span className={SECTION_LABEL}>Contact &amp; links</span>

          {/* Email — plain text row with copy (omitted when null) */}
          {candidate.email ? (
            <div className="flex items-center gap-1 text-[11px] text-text-secondary">
              <MailIcon className="shrink-0 text-text-tertiary" />
              <a
                href={`mailto:${candidate.email}`}
                className="truncate text-text-link hover:underline"
                data-testid="candidate-sidebar-email"
              >
                {candidate.email}
              </a>
              <CopyButton
                value={candidate.email}
                toastLabel={`${firstName}'s email`}
                ariaLabel={`Copy ${firstName}'s email`}
              />
            </div>
          ) : null}

          {/* Phone — plain text row with copy (omitted when null) */}
          {candidate.phone ? (
            <div className="flex items-center gap-1 text-[11px] text-text-secondary">
              <PhoneIcon className="shrink-0 text-text-tertiary" />
              <a
                href={`tel:${candidate.phone}`}
                className="truncate text-text-link hover:underline"
                data-testid="candidate-sidebar-phone"
              >
                {candidate.phone}
              </a>
              <CopyButton
                value={candidate.phone}
                toastLabel={`${firstName}'s phone`}
                ariaLabel={`Copy ${firstName}'s phone`}
              />
            </div>
          ) : null}

          {/* Download Resume — brand-gradient pill (§8.2 item 5) */}
          {candidate.storage_key_present ? (
            <a
              href={`/api/t/${tenantSlug}/candidates/${candidate.id}/resume`}
              download={resumeFilename}
              data-testid="candidate-sidebar-resume"
              className="inline-flex items-center gap-1.5 rounded-[5px] bg-brand-grad px-[11px] py-[5px] text-[11px] font-semibold text-white transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]"
            >
              <DownloadIcon />
              Download Resume
            </a>
          ) : null}

          {/* LinkedIn Profile — LinkedIn-blue pill (§8.2 item 5) */}
          {showLinkedIn ? (
            <a
              href={candidate.linkedin_url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="candidate-sidebar-linkedin"
              style={{ background: LINKEDIN_BLUE }}
              className="inline-flex items-center gap-1.5 rounded-[5px] px-[11px] py-[5px] text-[11px] font-semibold text-white transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]"
            >
              <LinkedInIcon />
              LinkedIn Profile
            </a>
          ) : null}
        </div>
      ) : null}

      {/* Notes subsection (Q4 — quick-add textarea AT TOP per §8.2 item 6).
          Order: textarea → button → "Notes" label → recent 3 notes → "+ N more →".
          P2-2.5-sidebar-notes-summary: real `useNotes` wiring; cache is
          shared with the Notes tab so tab activation does not refetch. */}
      <div
        className="flex flex-col gap-1.5"
        data-testid="candidate-sidebar-notes"
        id="cp-notes-sidebar"
      >
        {/* Quick-add textarea — FIRST per Q4 (top of Notes subsection) */}
        <textarea
          id="sidebar-note-ta"
          data-testid="candidate-sidebar-note-textarea"
          className="note-add-ta w-full resize-none rounded border border-border-strong bg-surface-card p-2 text-[11px] text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none"
          style={{ height: "70px" }}
          placeholder="Add a note…"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          disabled={createNote.isPending}
        />

        {/* Add Note button */}
        <button
          type="button"
          data-testid="candidate-sidebar-note-add-btn"
          onClick={handleAddNote}
          disabled={createNote.isPending || !noteText.trim()}
          className="pi-btn self-start rounded-md bg-brand-grad px-3 py-[5px] text-[11px] font-semibold text-white hover:brightness-110 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]"
        >
          {createNote.isPending ? "Adding…" : "Add Note"}
        </button>

        {/* Notes label — below textarea+button per Q4 ordering */}
        <span className={`${SECTION_LABEL} mt-1`}>Notes</span>

        {/* Recent 3 notes */}
        {hasNotes ? (
          <>
            <ul
              className="flex flex-col gap-2"
              data-testid="candidate-sidebar-notes-list"
            >
              {recentNotes.map((note) => (
                <li
                  key={note.id}
                  className="note-mini flex flex-col gap-0.5 rounded-[7px] border border-border-default bg-surface-card px-[11px] py-[9px]"
                  data-testid="candidate-sidebar-note-row"
                >
                  <p
                    className="note-mini-txt text-[11px] leading-[1.5] text-text-secondary"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {note.content}
                  </p>
                  <time
                    dateTime={note.created_at}
                    className="note-mini-date mt-1 text-[9px] text-text-tertiary"
                  >
                    {relativeTime(note.created_at)}
                  </time>
                </li>
              ))}
            </ul>
            {/* "+ N more →" link — shown when there are more than 3 notes */}
            {totalNotes > 3 && onAllNotesClick ? (
              <button
                type="button"
                onClick={onAllNotesClick}
                data-testid="candidate-sidebar-notes-all-link"
                className="mt-1 self-start text-[11px] font-medium text-accent-secondary hover:underline"
              >
                + {totalNotes - 3} more →
              </button>
            ) : onAllNotesClick ? (
              <button
                type="button"
                onClick={onAllNotesClick}
                data-testid="candidate-sidebar-notes-all-link"
                className="mt-1 self-start text-xs font-medium text-text-link hover:underline"
              >
                All notes →
              </button>
            ) : null}
          </>
        ) : notesLoaded ? (
          <p
            className="text-xs text-text-tertiary"
            data-testid="candidate-sidebar-notes-empty"
          >
            No notes yet.
          </p>
        ) : null}
      </div>
    </aside>
  );
}
